import { TFunction } from 'i18next'

import { Trainer, TrainerService, TrainerServiceType } from '@/api/trainers'
import { SelectOption } from '@/components/ui/select'
import { SortOrder } from '@/components/ui/table'
import { formatPrice } from '@/utils/currency'
import { formatPhoneNumberDisplay } from '@/utils/phone-number'

import { filterBySearch, getPersonFullName, sortItems } from '../shared/person-table.utils'
import { TrainerSortKey } from './staff.sort-keys'
import { TrainerStatusFilter } from './staff.status-filter'

export interface ResolvedTrainerService extends TrainerService {
  serviceId: TrainerServiceType
}

// Note: BE sends serviceId as null for a service whose training type was deleted - such rows have nothing to show or re-send
export const getTrainerServices = (trainer: Pick<Trainer, 'services'>) =>
  trainer.services.filter((service): service is ResolvedTrainerService => !!service.serviceId?.name)

// Note: a percentage is shown as 20%, a flat rate as a price ($15.00)
export const formatTrainerServiceFee = (service: Pick<TrainerService, 'fee' | 'percentage'>) =>
  service.percentage
    ? `${service.fee}%`
    : formatPrice(Math.round(service.fee * 100), { zeroPrice: true })

export const getTrainerFullName = (trainer: Trainer) => getPersonFullName(trainer.user)

export const getTrainerGenderLabel = (t: TFunction<['organisation']>, gender: string | null) => {
  if (gender === 'Male') return t('organisation:staff.form.gender.male')
  if (gender === 'Female') return t('organisation:staff.form.gender.female')
  return gender ?? ''
}

export const filterTrainersByStatus = (trainers: Trainer[], status?: TrainerStatusFilter) => {
  if (!status || status === TrainerStatusFilter.all) return trainers

  return trainers.filter((trainer) => trainer.active === (status === TrainerStatusFilter.active))
}

export const filterTrainersByBuildings = (trainers: Trainer[], buildingIds?: number[]) => {
  if (!buildingIds?.length) return trainers

  return trainers.filter((trainer) =>
    trainer.buildingIds.some((building) => buildingIds.includes(building.id)),
  )
}

export const getTrainerBuildingOptions = (trainers: Trainer[]): SelectOption<number>[] => {
  const buildingsById = new Map<number, string>()
  trainers.forEach((trainer) =>
    trainer.buildingIds.forEach((building) => buildingsById.set(building.id, building.name)),
  )

  return [...buildingsById]
    .map(([value, label]) => ({ label, value }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const filterTrainers = (trainers: Trainer[], search?: string) =>
  filterBySearch(
    trainers,
    search,
    (trainer) => [
      getTrainerFullName(trainer),
      trainer.user.email,
      ...getTrainerServices(trainer).map((service) => service.serviceId.name),
    ],
    (trainer) => trainer.phone,
  )

const getSortValue = (trainer: Trainer, sortBy: TrainerSortKey) => {
  switch (sortBy) {
    case TrainerSortKey.clients:
      return trainer.clients.length
    case TrainerSortKey.createdAt:
      return trainer.createdAt
    case TrainerSortKey.email:
      return trainer.user.email.toLowerCase()
    case TrainerSortKey.gender:
      return (trainer.gender ?? '').toLowerCase()
    case TrainerSortKey.name:
      return getTrainerFullName(trainer).toLowerCase()
    case TrainerSortKey.phone:
      return trainer.phone ?? 0
    case TrainerSortKey.services:
      return getTrainerServices(trainer).length
    case TrainerSortKey.status:
      return trainer.active ? 0 : 1
  }
}

export const sortTrainers = (
  trainers: Trainer[],
  sortBy?: TrainerSortKey | null,
  sortOrder?: SortOrder | null,
) => sortItems(trainers, sortBy, sortOrder, getSortValue)

export const getTrainersCsvFields = (t: TFunction<['organisation']>) => [
  {
    label: t('organisation:staff.list.csv.firstName'),
    value: (row: Trainer) => row.user.firstName,
  },
  {
    label: t('organisation:staff.list.csv.lastName'),
    value: (row: Trainer) => row.user.lastName,
  },
  {
    label: t('organisation:staff.list.csv.email'),
    value: (row: Trainer) => row.user.email,
  },
  {
    label: t('organisation:staff.list.csv.phone'),
    value: (row: Trainer) => formatPhoneNumberDisplay(row.phone),
  },
  {
    label: t('organisation:staff.list.csv.gender'),
    value: (row: Trainer) => getTrainerGenderLabel(t, row.gender),
  },
  {
    label: t('organisation:staff.list.csv.address'),
    value: (row: Trainer) => row.street ?? '',
  },
  {
    label: t('organisation:staff.list.csv.apartmentNumber'),
    value: (row: Trainer) => row.apartment ?? '',
  },
  {
    label: t('organisation:staff.list.csv.zipCode'),
    value: (row: Trainer) => row.zipCode ?? '',
  },
  {
    label: t('organisation:staff.list.csv.services'),
    value: (row: Trainer) =>
      getTrainerServices(row)
        .map((service) => service.serviceId.name)
        .join('; '),
  },
  {
    label: t('organisation:staff.list.csv.status'),
    value: (row: Trainer) =>
      row.active
        ? t('organisation:shared.status.active')
        : t('organisation:shared.status.inactive'),
  },
]
